import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import Dashboard from './components/Dashboard';
import LogEntry from './components/LogEntry';
import SystemsGuide from './components/SystemsGuide';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import './App.css';

const { FiBarChart3, FiEdit3, FiBook, FiMenu, FiX, FiPlus, FiTarget } = FiIcons;

function Navigation() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Dashboard', icon: FiBarChart3 },
    { path: '/systems-guide', label: 'Systems Guide', icon: FiBook }
  ];

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo and Brand Section */}
          <div className="flex items-center space-x-4">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="bg-purple-600 rounded-lg p-2 shadow-sm group-hover:bg-purple-700 transition-colors">
                <SafeIcon icon={FiTarget} className="w-6 h-6 text-white" />
              </div>
              <div className="flex flex-col">
                <div className="text-xl font-bold leading-none">
                  <span className="text-gray-900">Clarity</span> <span className="text-purple-600">Ecosystem</span>
                </div>
                <span style={{
      color: 'rgb(107, 114, 128)'
    }} className="text-xs text-gray-500 mt-1 font-medium">Measure engagement, audience response, and the strength of your connection points.</span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div style={{
      color: 'rgb(147, 51, 234)'
    }} className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'text-gray-900 bg-gray-50'
                    : 'text-gray-600 hover:text-purple-600 hover:bg-gray-50'
                }`}
              >
                <SafeIcon icon={item.icon} className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
            
            {/* New Entry Button */}
            <Link style={{
      color: 'rgb(147, 51, 234)'
    }}
              to="/log-entry"
              state={{ createNew: true }}
              className="bg-green-50 hover:bg-green-100 text-green-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center space-x-2 border border-transparent hover:border-green-200"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>New Entry</span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-600 hover:text-gray-900 focus:outline-none p-2 rounded-md hover:bg-gray-100"
            >
              <SafeIcon icon={isMobileMenuOpen ? FiX : FiMenu} className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 space-y-2 bg-white absolute left-0 right-0 shadow-lg px-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-purple-50 text-purple-700'
                    : 'text-gray-600 hover:text-purple-600 hover:bg-gray-50'
                }`}
              >
                <SafeIcon icon={item.icon} className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            ))}
            <div className="pt-2">
              <Link
                to="/log-entry"
                state={{ createNew: true }}
                onClick={() => setIsMobileMenuOpen(false)}
                className="w-full bg-green-50 hover:bg-green-100 text-green-700 px-4 py-3 rounded-lg text-sm font-bold transition-colors flex items-center justify-center space-x-2 border border-green-100"
              >
                <SafeIcon icon={FiPlus} className="w-5 h-5" />
                <span>New Entry</span>
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function App() {
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    const savedEntries = localStorage.getItem('connectionTrackerEntries');
    if (savedEntries) {
      setEntries(JSON.parse(savedEntries));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('connectionTrackerEntries', JSON.stringify(entries));
  }, [entries]);

  const addEntry = (entryData) => {
    const newEntry = {
      id: Date.now().toString(),
      ...entryData,
      log_count: 0,
      is_complete: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    setEntries(prev => [...prev, newEntry]);
    return newEntry.id;
  };

  const updateEntry = (id, updates) => {
    setEntries(prev => prev.map(entry => 
      entry.id === id 
        ? { ...entry, ...updates, updated_at: new Date().toISOString() }
        : entry
    ));
  };

  const deleteEntry = (id) => {
    setEntries(prev => prev.filter(entry => entry.id !== id));
  };

  const logResults = (id, logData) => {
    setEntries(prev => prev.map(entry => {
      if (entry.id === id) {
        return {
          ...entry,
          ...logData,
          log_count: entry.log_count + 1,
          updated_at: new Date().toISOString()
        };
      }
      return entry;
    }));
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Navigation />
        <main className="flex-grow max-w-7xl mx-auto w-full py-8 px-4 sm:px-6 lg:px-8">
          <Routes>
            <Route 
              path="/" 
              element={
                <Dashboard 
                  entries={entries}
                  updateEntry={updateEntry}
                  deleteEntry={deleteEntry}
                />
              } 
            />
            <Route 
              path="/log-entry" 
              element={
                <LogEntry 
                  entries={entries}
                  addEntry={addEntry}
                  logResults={logResults}
                />
              } 
            />
            <Route path="/systems-guide" element={<SystemsGuide />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;